/**
 * Success and failure toasts for the panel's write operations.
 *
 * Every create, edit and delete ends in one of these, so the person gets the same confirmation
 * everywhere instead of having to infer success from a list that re-rendered. Wrapping `addToast`
 * keeps the wording and the type consistent, and keeps the error-message extraction in one place.
 *
 * @module Hooks
 */

import { useToast } from '@group-link-one/gl-components';
import { useCallback, useMemo } from 'react';

export interface ActionToast {
  /** Something was created, saved, deleted or restored. */
  success: (title: string, message?: string) => void;
  /**
   * An operation failed.
   *
   * The service layer throws `Error`s carrying the BFF's own message, which is more useful than a
   * generic phrase — so it is shown when there is one, and `fallback` covers the rest.
   */
  failure: (title: string, error: unknown, fallback?: string) => void;
}

export function useActionToast(): ActionToast {
  const { addToast } = useToast();

  const success = useCallback(
    (title: string, message?: string) => {
      addToast({ type: 'success', title, ...(message ? { message } : {}) });
    },
    [addToast],
  );

  const failure = useCallback(
    (title: string, error: unknown, fallback = 'Tente de novo em instantes.') => {
      const message = error instanceof Error && error.message ? error.message : fallback;
      addToast({ type: 'error', title, message });
    },
    [addToast],
  );

  return useMemo(() => ({ success, failure }), [success, failure]);
}
